import React, { useState, useEffect } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import { motion, AnimatePresence } from "framer-motion";
import YouTubeVideoCarousel from "./YouTubeVideoCarousel";
import About from "./About";

const PrimeX = () => {
  const [productData, setProductData] = useState({});
  const [directions, setDirections] = useState([]);
  const [selectedDirection, setSelectedDirection] = useState({});
  const [selectedSize, setSelectedSize] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentColorIndex, setCurrentColorIndex] = useState(0);
  const [currentImages, setCurrentImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [expandedImageSrc, setExpandedImageSrc] = useState("");

  // Fetch product data by ID
  const fetchProductById = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/66fe71c926a786dcf66e891d`
      );
      setProductData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching product data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductById();
  }, []);

  useEffect(() => {
    if (productData?.products?.length > 0) {
      const newDirections = Object.keys(productData.products[0].direction).map(
        (directionKey) => {
          const sizes = productData.products[0].direction[directionKey].sizes;
          return {
            title: directionKey,
            sizes,
          };
        }
      );

      setDirections(newDirections);
      setSelectedDirection(newDirections[0]);
      const firstSize = Object.keys(newDirections[0].sizes)[0];
      setSelectedSize(newDirections[0].sizes[firstSize]);
      setCurrentImages(newDirections[0].sizes[firstSize].colors["white"] || []);
    }
  }, [productData]);

  useEffect(() => {
    if (selectedSize.colors) {
      const colorKeys = Object.keys(selectedSize.colors);
      const totalColors = colorKeys.length;
      const totalImages =
        selectedSize.colors[colorKeys[currentColorIndex]].length;

      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => {
          if (prevIndex + 1 < totalImages) {
            return prevIndex + 1;
          } else {
            setCurrentColorIndex(
              (prevColorIndex) => (prevColorIndex + 1) % totalColors
            );
            setCurrentImages(
              selectedSize.colors[
                colorKeys[(currentColorIndex + 1) % totalColors]
              ]
            );
            return 0;
          }
        });
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [currentColorIndex, selectedSize]);

  const handleDirectionChange = (direction) => {
    setSelectedDirection(direction);
    const firstSize = Object.keys(direction.sizes)[0];
    setSelectedSize(direction.sizes[firstSize]);
    setCurrentImages(direction.sizes[firstSize].colors["white"] || []);
    setCurrentColorIndex(0);
    setCurrentImageIndex(0);
  };

  const handleSizeChange = (sizeKey) => {
    setSelectedSize(selectedDirection.sizes[sizeKey]);
    setCurrentImages(selectedDirection.sizes[sizeKey].colors["white"] || []);
    setCurrentColorIndex(0);
    setCurrentImageIndex(0);
  };

  const handleColorChange = (color) => {
    if (selectedSize.colors && selectedSize.colors[color]) {
      setCurrentImages(selectedSize.colors[color]);
      setCurrentColorIndex(Object.keys(selectedSize.colors).indexOf(color));
      setCurrentImageIndex(0);
    }
  };

  const openFullScreen = () => {
    setExpandedImageSrc(currentImages[currentImageIndex]);
    setShowFullScreen(true);
  };

  return (
    <div className="mx-auto px-2 py-5 max-w-6xl">
      {/* Full-Screen Modal for Expanded Image */}
      <AnimatePresence>
        {showFullScreen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="relative space-y-5">
              <button
                onClick={() => setShowFullScreen(false)}
                className="absolute top-7 right-3 text-black text-4xl     focus:outline-none"
              >
                &times;
              </button>
              <img
                src={expandedImageSrc}
                alt="Full Screen Product"
                className="w-full h-auto max-w-screen-lg max-h-screen-lg object-contain"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Breadcrumb Navigation */}
      <nav className="flex mb-4" aria-label="Breadcrumb">
        <ol
          id="award"
          className="inline-flex items-center space-x-1 text-[10px] md:space-x-3 font-mono text-black"
        >
          <li>
            <a
              href="/"
              className="font-semibold text-black hover:text-gray-600 transition-colors duration-200"
            >
              Home
            </a>
          </li>
          <li className="font-light text-gray-500">/</li>
          <li className="font-semibold text-black hover:text-gray-600 transition-colors duration-200">
            {loading ? <Skeleton width={100} /> : productData?.category}
          </li>
          <li className="font-light text-gray-500">/</li>
          <li className="font-semibold text-black hover:text-gray-600 transition-colors duration-200">
            Deep Junction
          </li>
        </ol>
      </nav>

      {/* Product Details */}
      <div className="flex flex-col md:flex-row">
        <div className="md:w-1/2 mb-4 md:mb-0">
          {/* Main Big Image */}
          <div className="flex justify-center">
            {loading ? (
              <Skeleton height={300} width={300} />
            ) : (
              <img
                src={currentImages[currentImageIndex] || ""}
                alt="Selected Product"
                className="w-full h-auto drop-shadow-xl shadow-lg object-contain rounded-md cursor-pointer"
                onClick={openFullScreen} // Click to expand
              />
            )}
          </div>
          <div className="flex justify-center items-center mt-4 space-x-2">
            {loading
              ? [...Array(3)].map((_, index) => (
                  <Skeleton key={index} width={80} height={60} />
                ))
              : currentImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Product Image ${index + 1}`}
                    className={`w-15 h-16 rounded-md cursor-pointer transition-transform transform ${
                      currentImageIndex === index
                        ? "border-2 border-customBlue scale-105"
                        : "border border-gray-300 hover:scale-105"
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
          </div>
        </div>

        <div className="md:w-1/2 md:pl-8">
          <h1
            id="award"
            className="font-bold text-xl font-mono  text-customBlue"
          >
            {loading ? <Skeleton width={200} /> : productData?.name}
          </h1>

          {/* Direction Selection */}
          <div className="mt-4">
            <h2 className="text-lg font-bold font-mono mb-2 text-black">
              Select Direction:
            </h2>
            <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {loading
                ? [...Array(3)].map((_, index) => (
                    <li key={index}>
                      <Skeleton width={100} height={40} />
                    </li>
                  ))
                : directions.map((direction) => (
                    <li key={direction.title}>
                      <button
                        className={`w-full py-2 px-3 rounded-md border uppercase text-center transition-all ${
                          selectedDirection.title === direction.title
                            ? " bg-customBlue text-white"
                            : "bg-white text-black border-gray-300 hover:bg-gray-100"
                        }`}
                        onClick={() => handleDirectionChange(direction)}
                      >
                        {direction.title}
                      </button>
                    </li>
                  ))}
            </ul>
          </div>

          {/* Size Selection */}
          <div className="mt-4">
            <h2 className="text-lg font-bold font-mono mb-2 text-black">
              Select Size:
            </h2>
            <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {loading
                ? [...Array(3)].map((_, index) => (
                    <li key={index}>
                      <Skeleton width={100} height={40} />
                    </li>
                  ))
                : selectedDirection?.sizes &&
                  Object.keys(selectedDirection.sizes).map(
                    (sizeKey, index, array) => (
                      <li key={sizeKey}>
                        <button
                          className={`w-full py-2 px-3 rounded-md border uppercase text-center transition-all ${
                            selectedSize === selectedDirection.sizes[sizeKey]
                              ? " bg-customBlue text-white"
                              : "bg-white text-black border-gray-300 hover:bg-gray-100"
                          } ${
                            index === array.length - 1 && array.length % 2 === 1
                              ? "w-full"
                              : "w-auto"
                          }`}
                          onClick={() => handleSizeChange(sizeKey)}
                        >
                          {sizeKey}
                        </button>
                      </li>
                    )
                  )}
            </ul>
          </div>

          {/* Color Selection */}
          <div className="mt-4">
            <h2 className="text-lg font-bold font-mono mb-2 text-black">
              Select Color:
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {loading
                ? [...Array(3)].map((_, index) => (
                    <li key={index}>
                      <Skeleton width={100} height={40} />
                    </li>
                  ))
                : selectedSize.colors &&
                  Object.keys(selectedSize.colors).map((color) => (
                    <button
                      key={color}
                      className={`p-2 border rounded ${
                        currentColorIndex ===
                        Object.keys(selectedSize.colors).indexOf(color)
                          ? "bg-customBlue text-white"
                          : "bg-white text-black"
                      }`}
                      onClick={() => handleColorChange(color)}
                    >
                      {color.charAt(0).toUpperCase() + color.slice(1)}
                    </button>
                  ))}
            </ul>
          </div>

          {/* Description Section */}
          <div className="my-4">
            <h2
              id="award"
              className="text-sm font-bold font-mono mt-4 mb-3 text-black"
            >
              Description
            </h2>
            <p className="text-gray-700 text-sm">
              {loading ? (
                <Skeleton count={3} />
              ) : (
                productData?.products?.[0]?.description ||
                "No description available."
              )}
            </p>
          </div>

          {/* Additional Components */}
          <About />
          <YouTubeVideoCarousel
            ytLinks={
              productData?.ytLinks
                ? productData.ytLinks.map((yt) => yt.link)
                : []
            }
          />
        </div>
      </div>
    </div>
  );
};

export default PrimeX;
